<script setup lang="ts">
import { ref } from "vue";

const needRefresh = ref(false);
const offlineReady = ref(false);
let updateServiceWorker: (reloadPage?: boolean) => Promise<void> = () =>
  Promise.resolve();

// 注册PWA更新监听
if ("serviceWorker" in navigator) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    updateServiceWorker = registerSW({
      onNeedRefresh() {
        needRefresh.value = true;
      },
      onOfflineReady() {
        offlineReady.value = true;
        setTimeout(() => {
          offlineReady.value = false;
        }, 3000);
      },
      onRegistered() {
        console.log("Service Worker 已注册");
      },
      onRegisterError(error) {
        console.error("Service Worker 注册失败:", error);
      },
    });
  });
}

const updateApp = async () => {
  await updateServiceWorker(true);
  needRefresh.value = false;
};

const closeUpdatePrompt = () => {
  needRefresh.value = false;
};

const closeOfflinePrompt = () => {
  offlineReady.value = false;
};
</script>

<template>
  <!-- 更新提示 -->
  <Transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="needRefresh"
      class="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4"
    >
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg
            class="h-6 w-6 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
        <div class="ml-3 w-0 flex-1">
          <p class="text-sm font-medium text-gray-900 dark:text-white">
            新版本可用
          </p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            点击更新以获取最新功能和修复
          </p>
          <div class="mt-3 flex space-x-3">
            <button
              @click="updateApp"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              立即更新
            </button>
            <button
              @click="closeUpdatePrompt"
              class="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none"
            >
              稍后
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- 离线就绪提示 -->
  <Transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="offlineReady"
      class="fixed bottom-4 left-4 z-50 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg shadow-lg p-4"
    >
      <div class="flex items-center">
        <svg
          class="h-5 w-5 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span class="ml-2 text-sm text-green-800 dark:text-green-200">
          应用已准备好离线使用
        </span>
        <button
          @click="closeOfflinePrompt"
          class="ml-4 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>
